import React, { useState, useEffect, useCallback, useRef } from "react";
import { GetData, PostData, PutData } from "../../../services/api";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./builder.css";
import VariableAutocomplete from "../../../components/VariableAutocomplete/VariableAutocomplete";
import useWorkflowVariables from "../../../Hooks/useWorkflowVariables";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  MarkerType,
  Handle,
  Position,
  BaseEdge,
  EdgeProps,
  getBezierPath,
  EdgeLabelRenderer,
  NodeToolbar,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import {
  Container,
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import Swal from 'sweetalert2';

// Helper function to delete node
const deleteNode = (nodeId) => {
  const reactFlowInstance = window.reactFlowInstance;
  if (reactFlowInstance && reactFlowInstance.deleteElements) {
    reactFlowInstance.deleteElements({ nodes: [{ id: nodeId }] });
  }
};

// Helper component for editable label
const EditableLabel = ({ value, onChange, placeholder = "Nombre del nodo" }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  const handleClick = (e) => {
    e.stopPropagation();
    // Solo activar edición si no está en modo edición
    if (!isEditing) {
      setIsEditing(true);
      setTempValue(value);
    }
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsEditing(true);
    setTempValue(value);
  };

  const handleBlur = () => {
    setIsEditing(false);
    const trimmedValue = tempValue.trim();
    if (trimmedValue !== value && trimmedValue !== '') {
      onChange(trimmedValue);
    } else if (trimmedValue === '') {
      setTempValue(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setTempValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        type="text"
        value={tempValue}
        onChange={(e) => setTempValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onDoubleClick={(e) => e.stopPropagation()}
        autoFocus
        style={{
          width: '100%',
          border: '1px solid #007bff',
          borderRadius: '4px',
          padding: '2px 6px',
          fontSize: '11px',
          background: 'white',
          outline: 'none',
          zIndex: 1000,
          position: 'relative',
        }}
      />
    );
  }

  return (
    <div
      data-editable-label="true"
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
      style={{
        cursor: 'text',
        minHeight: '18px',
        padding: '2px 4px',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
        position: 'relative',
        zIndex: 10,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f0f0f0';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      title="Clic para editar"
    >
      {value || placeholder}
    </div>
  );
};

// Node types
const TriggerNode = ({ id, data, selected }) => {
  const { setNodes } = useReactFlow();

  const handleNameChange = (newName) => {
    setNodes((nds) => 
      nds.map((node) => 
        node.id === id 
          ? {
              ...node,
              data: {
                ...node.data,
                step: {
                  ...node.data.step,
                  name: newName,
                },
              },
            }
          : node
      )
    );
  };

  const stepName = data.step?.name || data.label || '';

  return (
    <>
      <NodeToolbar isVisible={selected} position={Position.Top} style={{ padding: 0 }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Trigger node cannot be deleted
          }}
          disabled
          style={{
            background: 'rgba(108, 117, 125, 0.5)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            cursor: 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            padding: 0,
          }}
          title="El trigger no se puede eliminar"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </NodeToolbar>
      <div className="workflow-node trigger-node">
        <Handle type="source" position={Position.Right} id="trigger-out-right" isConnectable={true} />
        <div className="node-header">
          <i className="ri-play-circle-line me-2"></i>
          <strong>Trigger</strong>
        </div>
        <div className="node-content">
          <EditableLabel 
            value={stepName} 
            onChange={handleNameChange}
            placeholder="Nombre del trigger"
          />
        </div>
        <Handle type="source" position={Position.Bottom} id="trigger-out" isConnectable={true} />
      </div>
    </>
  );
};

const ActionNode = ({ id, data, selected }) => {
  const { setNodes } = useReactFlow();

  const handleNameChange = (newName) => {
    setNodes((nds) => 
      nds.map((node) => 
        node.id === id 
          ? {
              ...node,
              data: {
                ...node.data,
                step: {
                  ...node.data.step,
                  name: newName,
                },
              },
            }
          : node
      )
    );
  };

  const stepName = data.step?.name || data.label || '';

  return (
    <>
      <NodeToolbar isVisible={selected} position={Position.Top} style={{ padding: 0 }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteNode(id);
          }}
          style={{
            background: 'rgba(255, 68, 68, 0.85)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            padding: 0,
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#ff4444';
            e.currentTarget.style.transform = 'scale(1.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 68, 68, 0.85)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="Eliminar nodo"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </NodeToolbar>
      <div className="workflow-node action-node">
        <Handle type="target" position={Position.Left} id="action-in-left" isConnectable={true} />
        <Handle type="target" position={Position.Top} id="action-in" isConnectable={true} />
        <div className="node-header">
          <i className="ri-settings-3-line me-2"></i>
          <strong>Acción</strong>
        </div>
        <div className="node-content">
          <EditableLabel 
            value={stepName} 
            onChange={handleNameChange}
            placeholder="Nombre de la acción"
          />
        </div>
        <Handle type="source" position={Position.Bottom} id="action-out" isConnectable={true} />
        <Handle type="source" position={Position.Right} id="action-out-right" isConnectable={true} />
      </div>
    </>
  );
};

const ConditionNode = ({ id, data, selected }) => {
  const { setNodes } = useReactFlow();

  const handleNameChange = (newName) => {
    setNodes((nds) => 
      nds.map((node) => 
        node.id === id 
          ? {
              ...node,
              data: {
                ...node.data,
                step: {
                  ...node.data.step,
                  name: newName,
                },
              },
            }
          : node
      )
    );
  };

  const stepName = data.step?.name || data.label || '';

  return (
    <>
      <NodeToolbar isVisible={selected} position={Position.Top} style={{ padding: 0 }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteNode(id);
          }}
          style={{
            background: 'rgba(255, 68, 68, 0.85)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            padding: 0,
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#ff4444';
            e.currentTarget.style.transform = 'scale(1.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 68, 68, 0.85)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="Eliminar nodo"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </NodeToolbar>
      <div className="workflow-node condition-node">
        <Handle type="target" position={Position.Left} id="condition-in-left" isConnectable={true} />
        <Handle type="target" position={Position.Top} id="condition-in" isConnectable={true} />
        <div className="node-header">
          <i className="ri-question-line me-2"></i>
          <strong>Condición</strong>
        </div>
        <div className="node-content">
          <EditableLabel 
            value={stepName} 
            onChange={handleNameChange}
            placeholder="Nombre de la condición"
          />
        </div>
        <div className="node-handles">
          <Handle 
            type="source" 
            position={Position.Bottom} 
            id="condition-true" 
            style={{ left: '30%' }}
            isConnectable={true}
          />
          <Handle 
            type="source" 
            position={Position.Bottom} 
            id="condition-false" 
            style={{ left: '70%' }}
            isConnectable={true}
          />
          <Handle 
            type="source" 
            position={Position.Right} 
            id="condition-out-right" 
            isConnectable={true}
          />
          <div className="handle-label true-label">Sí</div>
          <div className="handle-label false-label">No</div>
        </div>
      </div>
    </>
  );
};

const WaitNode = ({ id, data, selected }) => {
  const { setNodes } = useReactFlow();

  const handleNameChange = (newName) => {
    setNodes((nds) => 
      nds.map((node) => 
        node.id === id 
          ? {
              ...node,
              data: {
                ...node.data,
                step: {
                  ...node.data.step,
                  name: newName,
                },
              },
            }
          : node
      )
    );
  };

  const stepName = data.step?.name || data.label || '';

  return (
    <>
      <NodeToolbar isVisible={selected} position={Position.Top} style={{ padding: 0 }}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteNode(id);
          }}
          style={{
            background: 'rgba(255, 68, 68, 0.85)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '10px',
            padding: 0,
            boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#ff4444';
            e.currentTarget.style.transform = 'scale(1.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 68, 68, 0.85)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="Eliminar nodo"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </NodeToolbar>
      <div className="workflow-node wait-node">
        <Handle type="target" position={Position.Left} id="wait-in-left" isConnectable={true} />
        <Handle type="target" position={Position.Top} id="wait-in" isConnectable={true} />
        <div className="node-header">
          <i className="ri-time-line me-2"></i>
          <strong>Espera</strong>
        </div>
        <div className="node-content">
          <EditableLabel 
            value={stepName} 
            onChange={handleNameChange}
            placeholder="Nombre de la espera"
          />
        </div>
        <Handle type="source" position={Position.Bottom} id="wait-out" isConnectable={true} />
        <Handle type="source" position={Position.Right} id="wait-out-right" isConnectable={true} />
      </div>
    </>
  );
};

// Custom Edge Component with delete button
const CustomEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={selected ? { ...style, strokeWidth: 2, stroke: style.stroke || '#555555' } : { ...style, stroke: style.stroke || '#555555', strokeWidth: style.strokeWidth || 1.5 }}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            left: `${labelX}px`,
            top: `${labelY}px`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'all',
            opacity: selected ? 1 : 0.6,
            zIndex: 10000,
            width: '18px',
            height: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          className="nodrag nopan"
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // Delete edge using ReactFlow instance
              const reactFlowInstance = window.reactFlowInstance;
              if (reactFlowInstance && reactFlowInstance.deleteElements) {
                reactFlowInstance.deleteElements({ edges: [{ id }] });
              }
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            style={{
              background: selected ? '#ff4444' : 'rgba(255, 68, 68, 0.8)',
              color: 'white',
              border: '1.5px solid white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.4)',
              fontSize: '10px',
              padding: 0,
              transition: 'all 0.15s ease',
              lineHeight: 1,
              minWidth: '18px',
              minHeight: '18px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ff0000';
              e.currentTarget.style.transform = 'scale(1.2)';
              e.currentTarget.style.boxShadow = '0 3px 6px rgba(0,0,0,0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = selected ? '#ff4444' : 'rgba(255, 68, 68, 0.8)';
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.4)';
            }}
            title="Eliminar transición"
          >
            <i className="ri-delete-bin-line" style={{ fontSize: '10px', lineHeight: 1, display: 'block' }}></i>
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const edgeTypes = {
  default: CustomEdge,
  smoothstep: CustomEdge,
};

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  wait: WaitNode,
};

const WorkflowBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new' || id === 'undefined';
  
  const [workflow, setWorkflow] = useState({
    name: "",
    description: "",
    status: "draft",
    workflowTrigger: {
      type: "contact_created",
      conditions: {},
    },
    steps: [],
  });
  
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [configModal, setConfigModal] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeConfig, setNodeConfig] = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [salesTeams, setSalesTeams] = useState([]);
  
  // Undo/Redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoRef = useRef(false); // Track if we're doing undo/redo

  // Set ReactFlow instance
  const setReactFlowInstance = useCallback((instance) => {
    window.reactFlowInstance = instance;
  }, []);

  // Save state to history for undo/redo
  const saveToHistory = useCallback((currentNodes, currentEdges) => {
    // Don't save to history if we're doing undo/redo
    if (isUndoRedoRef.current) {
      return;
    }
    
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push({
        nodes: JSON.parse(JSON.stringify(currentNodes)),
        edges: JSON.parse(JSON.stringify(currentEdges)),
      });
      // Limit history to 50 entries
      if (newHistory.length > 50) {
        newHistory.shift();
        return newHistory;
      }
      return newHistory;
    });
    setHistoryIndex((prev) => {
      const newIndex = prev + 1;
      // Limit history index
      return newIndex >= 50 ? 49 : newIndex;
    });
  }, [historyIndex]);

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoRef.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const state = history[newIndex];
      if (state) {
        setNodes(state.nodes);
        setEdges(state.edges);
      }
      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 100);
    }
  }, [history, historyIndex, setNodes, setEdges]);

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoRef.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const state = history[newIndex];
      if (state) {
        setNodes(state.nodes);
        setEdges(state.edges);
      }
      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isUndoRedoRef.current = false;
      }, 100);
    }
  }, [history, historyIndex, setNodes, setEdges]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd (Mac) or Ctrl (Windows/Linux)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;
      
      // Don't trigger if user is typing in an input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      if (modifier && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (modifier && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleUndo, handleRedo]);

  // Save to history when nodes or edges change (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (nodes.length > 0 || edges.length > 0) {
        saveToHistory(nodes, edges);
      }
    }, 300); // Debounce by 300ms

    return () => clearTimeout(timeoutId);
  }, [nodes, edges, saveToHistory]);

  useEffect(() => {
    if (!isNew && id) {
      fetchWorkflow();
    } else if (location.state?.imported) {
      // Flujo importado desde JSON (ids ya regenerados): cargarlo como flujo nuevo sin guardar
      const imported = location.state.imported;
      setLoading(false);
      setWorkflow({
        name: imported.name || "",
        description: imported.description || "",
        status: imported.status || "draft",
        workflowTrigger: imported.workflowTrigger || { type: "contact_created", conditions: {} },
        steps: imported.steps || [],
      });
      convertStepsToFlow(imported.steps || [], imported.workflowTrigger);
      // Limpiar el state para que un refresh no re-importe
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      setLoading(false);
      // Initialize with trigger node
      initializeFlow();
    }
  }, [id, isNew]);

  const initializeFlow = () => {
    const triggerNode = {
      id: 'trigger-1',
      type: 'trigger',
      position: { x: 250, y: 100 },
      data: {
        label: getTriggerLabel(workflow.workflowTrigger?.type || 'contact_created'),
        triggerType: workflow.workflowTrigger?.type || 'contact_created',
      },
      deletable: false, // Prevent trigger deletion
    };
    setNodes([triggerNode]);
    setEdges([]);
  };

  const getTriggerLabel = (type) => {
    const labels = {
      contact_created: 'Contacto Creado',
      contact_updated: 'Contacto Actualizado',
      contact_added_to_list: 'Agregado a Lista',
      contact_removed_from_list: 'Quitado de Lista',
      appointment_scheduled: 'Cita Agendada',
      email_opened: 'Email Abierto',
      email_clicked: 'Email Clickeado',
      scheduled: 'Programado',
    };
    return labels[type] || type;
  };

  const fetchWorkflow = async () => {
    if (!id || id === 'undefined' || id === 'new') {
      setLoading(false);
      initializeFlow();
      return;
    }

    try {
      setLoading(true);
      console.log("Fetching workflow with id:", id);
      const response = await GetData(`/workflows/${id}`);
      console.log("Workflow response:", response);
      
      const workflowData = response?.data || response;
      
      if (workflowData && workflowData.id) {
        setWorkflow({
          name: workflowData.name || "",
          description: workflowData.description || "",
          status: workflowData.status || "draft",
          workflowTrigger: workflowData.workflowTrigger || workflowData.trigger || {
            type: "contact_created",
            conditions: {},
          },
          steps: workflowData.steps || [],
        });

        // Convert steps to nodes and edges
        convertStepsToFlow(workflowData.steps || [], workflowData.workflowTrigger || workflowData.trigger);
      } else {
        throw new Error("No se recibieron datos válidos del flujo");
      }
    } catch (err) {
      console.error("Error fetching workflow:", err);
      const errorMessage = err.message || err.response?.data?.error?.message || "No se pudo cargar el flujo. Verifica que el flujo exista y que tengas permisos para accederlo.";
      Swal.fire({
        title: "Error",
        text: errorMessage,
        icon: "error"
      });
      navigate("/automation/workflows");
    } finally {
      setLoading(false);
    }
  };

  const convertStepsToFlow = (steps, trigger) => {
    const flowNodes = [];
    const flowEdges = [];

    // Add trigger node
    const triggerNode = {
      id: 'trigger-1',
      type: 'trigger',
      position: { x: 250, y: 100 },
      data: {
        label: getTriggerLabel(trigger?.type || 'contact_created'),
        triggerType: trigger?.type || 'contact_created',
      },
      deletable: false, // Prevent trigger deletion
    };
    flowNodes.push(triggerNode);

    // Create a map of step IDs to nodes for easy lookup
    const stepIdToNodeId = new Map();
    stepIdToNodeId.set('trigger-1', 'trigger-1');

    // Convert steps to nodes - use actual step.id
    let yPosition = 250;

    steps.forEach((step) => {
      const nodeId = step.id; // Use the actual step ID
      const nodeType = step.type === 'condition' ? 'condition' : step.type === 'wait' ? 'wait' : 'action';
      
      const nodeLabel = getStepLabel(step);
      
      const node = {
        id: nodeId,
        type: nodeType,
        position: step.position || { x: 250, y: yPosition },
        data: {
          label: nodeLabel,
          step: {
            ...step,
            name: step.name || nodeLabel, // Asegurar que tenga name
          },
          stepId: step.id,
        },
        deletable: true, // Allow node deletion (except trigger)
      };
      flowNodes.push(node);
      stepIdToNodeId.set(step.id, nodeId);
      yPosition += 150;
    });

    // Find all steps that have incoming connections
    const stepsWithIncoming = new Set();
    steps.forEach(step => {
      steps.forEach(s => {
        if (s.nextStepId === step.id || s.elseStepId === step.id) {
          stepsWithIncoming.add(step.id);
        }
      });
    });
    
    // Find the first step(s) - those without incoming connections
    const firstSteps = steps.filter(step => !stepsWithIncoming.has(step.id));
    
    // Connect first steps from trigger
    if (firstSteps.length > 0) {
      firstSteps.forEach(firstStep => {
        flowEdges.push({
          id: `e-trigger-1-${firstStep.id}`,
          source: 'trigger-1',
          target: firstStep.id,
          sourceHandle: 'trigger-out',
          targetHandle: firstStep.type === 'condition' ? 'condition-in' : firstStep.type === 'wait' ? 'wait-in' : 'action-in',
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#555555', strokeWidth: 1.5 },
          markerEnd: { type: MarkerType.ArrowClosed },
        });
      });
    }
    
    // Now create edges based on nextStepId and elseStepId for all steps
    steps.forEach((step) => {
      // Find incoming steps - look for steps that have this step as nextStepId or elseStepId
      const incomingSteps = steps.filter(s => 
        s.nextStepId === step.id || s.elseStepId === step.id
      );
      
      // Create edges from incoming steps (skip if already connected from trigger)
      if (incomingSteps.length > 0 && !firstSteps.includes(step)) {
        incomingSteps.forEach(incomingStep => {
          const isElsePath = incomingStep.elseStepId === step.id;
          const isNextPath = incomingStep.nextStepId === step.id;
          
          if (incomingStep.type === 'condition') {
            // Condition nodes have two outputs
            const sourceHandle = isNextPath ? 'condition-true' : 'condition-false';
            const label = isNextPath ? 'Sí' : 'No';
            
            flowEdges.push({
              id: `e-${incomingStep.id}-${step.id}-${isNextPath ? 'true' : 'false'}`,
              source: incomingStep.id,
              target: step.id,
              sourceHandle: sourceHandle,
              targetHandle: step.type === 'condition' ? 'condition-in' : step.type === 'wait' ? 'wait-in' : 'action-in',
              type: 'smoothstep',
              animated: true,
              label: label,
              style: { stroke: '#555555', strokeWidth: 1.5 },
              markerEnd: { type: MarkerType.ArrowClosed },
            });
          } else {
            // Regular nodes have one output
            const sourceHandle = incomingStep.type === 'wait' ? 'wait-out' :
                                 incomingStep.type === 'action' ? 'action-out' : undefined;
            
            flowEdges.push({
              id: `e-${incomingStep.id}-${step.id}`,
              source: incomingStep.id,
              target: step.id,
              sourceHandle: sourceHandle,
              targetHandle: step.type === 'condition' ? 'condition-in' : step.type === 'wait' ? 'wait-in' : 'action-in',
              type: 'smoothstep',
              animated: true,
              style: { stroke: '#555555', strokeWidth: 1.5 },
              markerEnd: { type: MarkerType.ArrowClosed },
            });
          }
        });
      }
    });

    setNodes(flowNodes);
    setEdges(flowEdges);
  };

  const getStepLabel = (step) => {
    switch (step.type) {
      case 'send_email':
        return `Enviar Email: ${step.config?.subject || 'Sin asunto'}`;
      case 'add_to_list':
        return `Agregar a Lista`;
      case 'remove_from_list':
        return `Remover de Lista`;
      case 'update_field':
        return `Actualizar Campo: ${step.config?.field || ''}`;
      case 'condition':
        return `Condición: ${step.config?.conditions?.length || 0} reglas`;
      case 'wait':
        return `Esperar ${step.config?.delay || 0} minutos`;
      case 'assign_to_sales_rep':
        const teamName = step.config?.salesTeamId ? 
          (salesTeams.find(t => t.id === step.config.salesTeamId)?.name || 'Equipo seleccionado') : 
          'Asignación automática';
        return `Asignar a Equipo: ${teamName}`;
      default:
        return step.type;
    }
  };

  const onConnect = useCallback((params) => {
    // Allow manual connections
    setEdges((eds) => {
      const newEdge = {
        ...params,
        type: 'smoothstep',
        animated: true,
        style: { strokeWidth: 1.5, stroke: '#555555' },
        markerEnd: { type: MarkerType.ArrowClosed },
        deletable: true,
      };
      const newEdges = addEdge(newEdge, eds);
      // Save to history after connection
      setTimeout(() => {
        saveToHistory(nodes, newEdges);
      }, 100);
      return newEdges;
    });
  }, [setEdges, nodes, saveToHistory]);

  const onEdgesDelete = useCallback((deletedEdges) => {
    // Allow deletion of edges
    setEdges((eds) => {
      const deletedIds = new Set(deletedEdges.map(e => e.id));
      const newEdges = eds.filter(e => !deletedIds.has(e.id));
      // Save to history after deletion
      setTimeout(() => {
        saveToHistory(nodes, newEdges);
      }, 100);
      return newEdges;
    });
  }, [setEdges, nodes, saveToHistory]);

  const onNodesDelete = useCallback((deletedNodes) => {
    // Prevent deletion of trigger node
    const nodesToDelete = deletedNodes.filter(n => n.id !== 'trigger-1');
    if (nodesToDelete.length === 0) {
      return;
    }
    
    setNodes((nds) => {
      const deletedIds = new Set(nodesToDelete.map(n => n.id));
      const newNodes = nds.filter(n => !deletedIds.has(n.id));
      // Save to history after deletion
      setTimeout(() => {
        saveToHistory(newNodes, edges);
      }, 100);
      return newNodes;
    });
    
    // Also delete connected edges
    setEdges((eds) => {
      const deletedIds = new Set(nodesToDelete.map(n => n.id));
      const newEdges = eds.filter(e => !deletedIds.has(e.source) && !deletedIds.has(e.target));
      return newEdges;
    });
  }, [setNodes, setEdges, edges, saveToHistory]);

  const handleAddNode = (type) => {
    const newNodeId = `step-${Date.now()}`;
    
    // Find the last node (excluding trigger) or use trigger as source
    const nonTriggerNodes = nodes.filter(n => n.id !== 'trigger-1');
    const lastNode = nonTriggerNodes.length > 0 
      ? nonTriggerNodes[nonTriggerNodes.length - 1] 
      : nodes.find(n => n.id === 'trigger-1');
    
    // Calculate position below the last node
    const yPosition = lastNode 
      ? (lastNode.position.y + 150) 
      : (nodes.length * 150 + 100);
    
    const newNode = {
      id: newNodeId,
      type: type,
      position: { 
        x: 250, 
        y: yPosition
      },
      data: {
        label: getDefaultLabel(type),
        step: {
          id: newNodeId,
          type: type === 'action' ? '' : type, // Para acciones, el tipo se selecciona después
          name: getDefaultLabel(type), // Agregar campo name
          config: getDefaultConfig(type),
        },
      },
      deletable: true, // Allow node deletion (except trigger)
    };
    
    // Add the new node
    setNodes((nds) => [...nds, newNode]);
    
    // NO auto-connect - all connections must be manual
    // User must manually drag from source handle to target handle
    
    // Open config modal
    setConfigModal(true);
    setSelectedNode(newNode);
    setNodeConfig(newNode.data.step);
  };

  const getDefaultLabel = (type) => {
    const labels = {
      action: 'Nueva Acción',
      condition: 'Nueva Condición',
      wait: 'Nueva Espera',
    };
    return labels[type] || 'Nuevo Paso';
  };

  const getDefaultConfig = (type) => {
    if (type === 'action') {
      return {}; // Will be set when user selects action type
    }
    switch (type) {
      case 'send_email':
        return { templateId: '', subject: '', emailProviderId: '' };
      case 'send_email_html':
        return { htmlContent: '', subject: '', emailProviderId: '', variables: {} };
      case 'send_whatsapp':
        return { whatsappTemplateId: '', phoneNumberId: '' };
      case 'webhook':
        return { webhookUrl: '', webhookMethod: 'POST', webhookHeaders: {}, webhookBody: '', webhookBodyType: 'json', webhookSendFullObject: false };
      case 'add_to_list':
      case 'remove_from_list':
        return { listId: '' };
      case 'update_field':
        return { field: '', value: '' };
      case 'condition':
        return { conditions: [], logic: 'AND' };
      case 'wait':
        return { delay: 0 };
      case 'assign_to_sales_rep':
        return { 
          salesTeamId: '', 
          sendEmailToAssignedMember: false,
          emailProviderId: '',
          fromEmail: '',
          emailSubject: 'Nuevo Lead Asignado: {{customerName}}',
          emailHtmlContent: ''
        };
      default:
        return {};
    }
  };

  const handleNodeClick = useCallback((event, node) => {
    console.log("Node clicked:", node);
    
    // Verificar si el clic viene de un input o del campo editable
    const target = event.target;
    if (target.tagName === 'INPUT' || target.closest('input') || target.closest('[data-editable-label]')) {
      return; // No abrir el modal si se está editando el nombre
    }
    
    event.stopPropagation();
    
    if (node.id === 'trigger-1') {
      // Trigger configuration is in the form above
      Swal.fire({
        title: "Configurar Trigger",
        text: "El trigger se configura en el formulario superior",
        icon: "info"
      });
      return;
    }
    
    setSelectedNode(node);
    const stepConfig = node.data.step || {
      id: node.id,
      type: node.type === 'action' ? '' : node.type === 'condition' ? 'condition' : node.type === 'wait' ? 'wait' : '',
      config: node.data.step?.config || getDefaultConfig(node.type),
    };
    setNodeConfig(stepConfig);
    setConfigModal(true);
  }, []);

  const handleSaveNodeConfig = () => {
    if (!selectedNode) return;

    const updatedNodes = nodes.map((node) => {
      if (node.id === selectedNode.id) {
        const updatedStep = {
          ...nodeConfig,
          id: node.id,
          type: nodeConfig.type || (node.type === 'action' ? '' : node.type === 'condition' ? 'condition' : node.type === 'wait' ? 'wait' : ''),
          name: nodeConfig.name || node.data.step?.name || getStepLabel({ ...nodeConfig, type: nodeConfig.type || node.type }), // Preservar name
          config: nodeConfig.config || {},
        };
        
        return {
          ...node,
          deletable: node.id !== 'trigger-1', // Only trigger is not deletable
          data: {
            ...node.data,
            label: getStepLabel(updatedStep),
            step: updatedStep,
          },
        };
      }
      return node;
    });

    setNodes(updatedNodes);
    setConfigModal(false);
    setSelectedNode(null);
    setNodeConfig({});
  };

  const convertFlowToSteps = () => {
    const stepNodes = nodes.filter((n) => n.id !== 'trigger-1');
    const stepsMap = new Map(); // Para búsqueda rápida
    
    // Primero, crear todos los steps con sus nextStepId/elseStepId
    stepNodes.forEach((node) => {
      if (node.data.step) {
        const step = {
          ...node.data.step,
          id: node.id,
          position: node.position,
        };

        // Find outgoing edges to determine next steps
        const outgoingEdges = edges.filter((e) => e.source === node.id);
        
        if (node.type === 'condition') {
          // Condition nodes have two paths: nextStepId (true) and elseStepId (false)
          const truePath = outgoingEdges.find(e => e.sourceHandle === 'condition-true');
          const falsePath = outgoingEdges.find(e => e.sourceHandle === 'condition-false');
          
          if (truePath) {
            step.nextStepId = truePath.target;
          }
          if (falsePath) {
            step.elseStepId = falsePath.target;
          }
        } else {
          // Regular nodes have one path: nextStepId
          if (outgoingEdges.length > 0) {
            // Use the first edge, or find the one connected to the main output handle
            const mainEdge = outgoingEdges.find(e => 
              e.sourceHandle === 'action-out' || 
              e.sourceHandle === 'wait-out' || 
              e.sourceHandle === 'trigger-out' ||
              !e.sourceHandle
            ) || outgoingEdges[0];
            
            if (mainEdge) {
              step.nextStepId = mainEdge.target;
            }
          }
        }

        stepsMap.set(step.id, step);
      }
    });

    // Encontrar el step inicial (el que no tiene ningún nextStepId/elseStepId apuntando a él)
    const stepsWithIncoming = new Set();
    stepsMap.forEach((step) => {
      if (step.nextStepId) {
        stepsWithIncoming.add(step.nextStepId);
      }
      if (step.elseStepId) {
        stepsWithIncoming.add(step.elseStepId);
      }
    });
    
    const initialSteps = Array.from(stepsMap.values()).filter(
      step => !stepsWithIncoming.has(step.id)
    );

    // Ordenar los steps siguiendo la lógica del workflow (BFS desde el step inicial)
    const orderedSteps = [];
    const visited = new Set();
    
    const addStep = (stepId) => {
      if (!stepId || visited.has(stepId)) return;
      
      const step = stepsMap.get(stepId);
      if (!step) return;
      
      visited.add(stepId);
      orderedSteps.push(step);
      
      // Agregar nextStepId si existe (camino principal primero)
      if (step.nextStepId) {
        addStep(step.nextStepId);
      }
      
      // Para condiciones, también agregar elseStepId (después de procesar nextStepId)
      if (step.elseStepId) {
        addStep(step.elseStepId);
      }
    };

    // Empezar desde los steps iniciales
    initialSteps.forEach(initialStep => {
      addStep(initialStep.id);
    });

    // Agregar cualquier step que no haya sido visitado (steps huérfanos o desconectados)
    stepsMap.forEach((step) => {
      if (!visited.has(step.id)) {
        orderedSteps.push(step);
      }
    });

    return orderedSteps;
  };

  const handleSave = async () => {
    if (!workflow.name.trim()) {
      Swal.fire({
        title: "Error",
        text: "El nombre del flujo es requerido",
        icon: "error"
      });
      return;
    }

    setSaving(true);
    try {
      const steps = convertFlowToSteps();
      const workflowData = {
        ...workflow,
        steps: steps,
      };

      if (isNew) {
        await PostData("/workflows", workflowData);
        Swal.fire("Flujo creado", "", "success");
      } else {
        await PutData(`/workflows/${id}`, workflowData);
        Swal.fire("Flujo actualizado", "", "success");
      }
      navigate("/automation/workflows");
    } catch (err) {
      Swal.fire("Error", err.message || "Error al guardar", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setConfigModal(false);
  };

  const handleTestStep = async () => {
    if (!selectedNode || !nodeConfig.type) {
      Swal.fire("Error", "Debes configurar el paso antes de probarlo", "error");
      return;
    }

    // For email and WhatsApp, ask for recipient
    const needsRecipient = ['send_email', 'send_email_html', 'send_whatsapp'].includes(nodeConfig.type);
    let testContactName = 'Juan Pérez';
    let testContactEmail = 'juan.perez@example.com';
    let testContactPhone = '+1234567890';

    if (needsRecipient) {
      const htmlContent = nodeConfig.type.includes('email') 
        ? `
          <div style="text-align: left;">
            <label style="display: block; margin-bottom: 5px; font-weight: bold;">Email Destinatario:</label>
            <input id="swal-email" class="swal2-input" type="email" placeholder="test@example.com" value="test@example.com" required>
            <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: bold;">Nombre del Contacto:</label>
            <input id="swal-name" class="swal2-input" type="text" placeholder="Juan Pérez" value="Juan Pérez">
          </div>
        `
        : `
          <div style="text-align: left;">
            <label style="display: block; margin-bottom: 5px; font-weight: bold;">Teléfono Destinatario:</label>
            <input id="swal-phone" class="swal2-input" type="tel" placeholder="+1234567890" value="+1234567890" required>
            <label style="display: block; margin-top: 15px; margin-bottom: 5px; font-weight: bold;">Nombre del Contacto:</label>
            <input id="swal-name" class="swal2-input" type="text" placeholder="Juan Pérez" value="Juan Pérez">
          </div>
        `;

      const result = await Swal.fire({
        title: 'Datos de Prueba',
        html: htmlContent,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Probar',
        cancelButtonText: 'Cancelar',
        preConfirm: () => {
          const emailInput = document.getElementById('swal-email');
          const phoneInput = document.getElementById('swal-phone');
          const nameInput = document.getElementById('swal-name');
          
          if (nodeConfig.type.includes('email')) {
            if (!emailInput || !emailInput.value) {
              Swal.showValidationMessage('El email es requerido');
              return false;
            }
            testContactEmail = emailInput.value;
          }
          
          if (nodeConfig.type === 'send_whatsapp') {
            if (!phoneInput || !phoneInput.value) {
              Swal.showValidationMessage('El teléfono es requerido');
              return false;
            }
            testContactPhone = phoneInput.value;
          }
          
          if (nameInput && nameInput.value) {
            testContactName = nameInput.value;
          }
          
          return true;
        },
      });

      if (result.isDismissed) {
        return; // User cancelled
      }
    }

    setTesting(true);
    setTestResult(null);

    try {
      // Create test contact data
      const testContactData = {
        id: 'test-contact-' + Date.now(),
        name: testContactName,
        email: testContactEmail,
        phone: testContactPhone,
        city: 'Ciudad de México',
        state: 'CDMX',
        attributes: {
          customField: 'Valor de prueba',
        },
      };

      // Get previous step results if any
      const previousStepResults = {};
      const incomingEdges = edges.filter(e => e.target === selectedNode.id);
      const previousNodes = incomingEdges.map(e => nodes.find(n => n.id === e.source)).filter(Boolean);
      
      previousNodes.forEach(node => {
        if (node.data.step && node.id !== selectedNode.id) {
          previousStepResults[node.id] = {
            sent: true,
            success: true,
            status: 200,
          };
        }
      });

      const stepToTest = {
        id: nodeConfig.id || selectedNode.id,
        type: nodeConfig.type,
        config: nodeConfig.config || {},
      };

      const response = await PostData('/workflows/steps/test', {
        step: stepToTest,
        contactData: testContactData,
        previousStepResults: previousStepResults,
      });

      setTestResult(response.data || response);
      
      const result = response.data || response;
      
      if (result.success && result.result) {
        const stepResult = result.result;
        if (stepResult.status === 'failed') {
          Swal.fire({
            title: "Error en la Prueba",
            html: `
              <div style="text-align: left; max-width: 600px;">
                <p><strong>Tipo:</strong> ${stepResult.stepType}</p>
                <p><strong>Estado:</strong> <span style="color: red;">${stepResult.status}</span></p>
                <p><strong>Contacto de Prueba:</strong></p>
                <ul>
                  <li>Nombre: ${stepResult.contactData?.name || 'N/A'}</li>
                  <li>Email: ${stepResult.contactData?.email || 'N/A'}</li>
                  <li>Teléfono: ${stepResult.contactData?.phone || 'N/A'}</li>
                </ul>
                <p><strong>Error:</strong></p>
                <div style="background: #fee; padding: 10px; border-radius: 4px; border-left: 4px solid #f00;">
                  <p style="margin: 0; color: #c00; font-weight: bold;">${stepResult.error || 'Error desconocido'}</p>
                  ${stepResult.errorDetails ? `<pre style="margin-top: 10px; font-size: 11px; color: #666; white-space: pre-wrap;">${stepResult.errorDetails}</pre>` : ''}
                </div>
                ${stepResult.result ? `<p><strong>Resultado parcial:</strong></p><pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; max-height: 200px; overflow-y: auto; font-size: 11px;">${JSON.stringify(stepResult.result, null, 2)}</pre>` : ''}
              </div>
            `,
            icon: "error",
            width: '700px',
          });
        } else {
          Swal.fire({
            title: "Prueba Exitosa",
            html: `
              <div style="text-align: left; max-width: 500px;">
                <p><strong>Tipo:</strong> ${stepResult.stepType}</p>
                <p><strong>Estado:</strong> <span style="color: green;">${stepResult.status}</span></p>
                <p><strong>Contacto de Prueba:</strong></p>
                <ul>
                  <li>Nombre: ${stepResult.contactData?.name || 'N/A'}</li>
                  <li>Email: ${stepResult.contactData?.email || 'N/A'}</li>
                  <li>Teléfono: ${stepResult.contactData?.phone || 'N/A'}</li>
                </ul>
                <p><strong>Resultado:</strong></p>
                <pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; max-height: 300px; overflow-y: auto; font-size: 12px;">${JSON.stringify(stepResult.result, null, 2)}</pre>
              </div>
            `,
            icon: "success",
            width: '600px',
          });
        }
      } else {
        Swal.fire({
          title: "Error en la Prueba",
          html: `
            <div style="text-align: left;">
              <p><strong>Error:</strong> ${result.error || result.message || 'Error desconocido'}</p>
              ${result.result?.error ? `<p><strong>Detalles:</strong> ${result.result.error}</p>` : ''}
            </div>
          `,
          icon: "error",
        });
      }
    } catch (error) {
      Swal.fire("Error", error.message || "Error al probar el paso", "error");
      setTestResult({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading && !isNew) {
    return (
      <div className="page-content">
        <Container fluid={true}>
          <div className="d-flex justify-content-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        {/* Compact Header Bar */}
        <Row className="mb-3">
          <Col>
            <Card>
              <CardBody style={{ padding: '12px 20px' }}>
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '15px',
                  flexWrap: 'wrap'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                    <Button 
                      color="light" 
                      size="sm"
                      onClick={() => navigate("/automation/workflows")}
                      style={{ minWidth: 'auto', padding: '6px 12px' }}
                    >
                      <i className="ri-arrow-left-line"></i>
                    </Button>
                    {/* Undo/Redo Buttons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Button
                        color="light"
                        size="sm"
                        onClick={handleUndo}
                        disabled={historyIndex <= 0}
                        style={{ 
                          minWidth: 'auto', 
                          padding: '6px 10px',
                          opacity: historyIndex <= 0 ? 0.5 : 1,
                          cursor: historyIndex <= 0 ? 'not-allowed' : 'pointer'
                        }}
                        title="Deshacer (Ctrl+Z / Cmd+Z)"
                      >
                        <i className="ri-arrow-go-back-line"></i>
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        onClick={handleRedo}
                        disabled={historyIndex >= history.length - 1}
                        style={{ 
                          minWidth: 'auto', 
                          padding: '6px 10px',
                          opacity: historyIndex >= history.length - 1 ? 0.5 : 1,
                          cursor: historyIndex >= history.length - 1 ? 'not-allowed' : 'pointer'
                        }}
                        title="Rehacer (Ctrl+Shift+Z / Cmd+Shift+Z)"
                      >
                        <i className="ri-arrow-go-forward-line"></i>
                      </Button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Label style={{ margin: 0, fontSize: '12px', color: '#6c757d', whiteSpace: 'nowrap' }}>Nombre:</Label>
                      <Input
                        type="text"
                        value={workflow.name}
                        onChange={(e) => setWorkflow({...workflow, name: e.target.value})}
                        placeholder="Nombre del flujo..."
                        style={{ 
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          padding: '6px 12px',
                          fontSize: '14px',
                          fontWeight: 500,
                          minWidth: '250px',
                          background: '#fff'
                        }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#6c757d' }}>
                      <Label style={{ margin: 0, whiteSpace: 'nowrap' }}>Trigger:</Label>
                      <Input
                        type="select"
                        value={workflow.workflowTrigger?.type || "contact_created"}
                        onChange={(e) => setWorkflow({
                          ...workflow,
                          workflowTrigger: {...(workflow.workflowTrigger || {conditions: {}}), type: e.target.value}
                        })}
                        style={{ 
                          fontSize: '12px', 
                          padding: '4px 8px',
                          height: '32px',
                          border: '1px solid #dee2e6',
                          borderRadius: '4px',
                          minWidth: '150px'
                        }}
                      >
                        <option value="contact_created">Contacto Creado</option>
                        <option value="contact_updated">Contacto Actualizado</option>
                        <option value="contact_added_to_list">Agregado a Lista</option>
                        <option value="contact_removed_from_list">Quitado de Lista</option>
                        <option value="appointment_scheduled">Cita Agendada</option>
                        <option value="email_opened">Email Abierto</option>
                        <option value="email_clicked">Email Clickeado</option>
                        <option value="scheduled">Programado</option>
                      </Input>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button 
                      color="success" 
                      size="sm"
                      onClick={handleSave} 
                      disabled={saving || !workflow.name}
                    >
                      <i className="ri-save-line me-1"></i>
                      {saving ? "Guardando..." : "Guardar"}
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Main Editor Area */}
      <Row>
        <Col lg={3}>
          <Card>
            <CardBody>
              <h5 className="mb-3">Agregar Paso</h5>
              <div className="d-grid gap-2">
                <Button 
                  color="primary" 
                  size="sm"
                  onClick={() => handleAddNode('action')}
                >
                  <i className="ri-settings-3-line me-2"></i>Acción
                </Button>
                <Button 
                  color="info" 
                  size="sm"
                  onClick={() => handleAddNode('condition')}
                >
                  <i className="ri-question-line me-2"></i>Condición
                </Button>
                <Button 
                  color="warning" 
                  size="sm"
                  onClick={() => handleAddNode('wait')}
                >
                  <i className="ri-time-line me-2"></i>Espera
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={9}>
          <Card>
            <CardBody style={{ height: '600px', padding: 0, position: 'relative' }}>
              <div style={{ width: '100%', height: '100%' }}>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onEdgesDelete={onEdgesDelete}
                  onNodesDelete={onNodesDelete}
                  onNodeClick={handleNodeClick}
                  onNodeDoubleClick={handleNodeClick}
                  onInit={setReactFlowInstance}
                  nodeTypes={nodeTypes}
                  edgeTypes={edgeTypes}
                  fitView
                  connectionLineStyle={{ stroke: '#007bff', strokeWidth: 2 }}
                  defaultEdgeOptions={{
                    type: 'smoothstep',
                    animated: true,
                    style: { strokeWidth: 1.5, stroke: '#555555' },
                    markerEnd: { type: MarkerType.ArrowClosed },
                    deletable: true, // Allow edge deletion
                  }}
                  edgesFocusable={true}
                  edgesUpdatable={false}
                  snapToGrid={true}
                  snapGrid={[20, 20]}
                  deleteKeyCode={['Backspace', 'Delete']} // Allow deletion with Backspace/Delete keys
                  selectNodesOnDrag={false} // Don't select nodes when dragging to avoid conflicts
                >
                  <Controls />
                  <MiniMap 
                    style={{ 
                      height: 120,
                      width: 150,
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #dee2e6'
                    }}
                  />
                  <Background variant="dots" gap={12} size={1} />
                </ReactFlow>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      </Container>

      {/* Node Configuration Modal */}
      <Modal isOpen={configModal} toggle={() => setConfigModal(false)} size="lg">
        <ModalHeader toggle={() => setConfigModal(false)}>
          Configurar Paso: {selectedNode?.data?.label}
        </ModalHeader>
        <ModalBody>
          {selectedNode && (
            <NodeConfigForm
              nodeType={selectedNode.type}
              config={nodeConfig}
              setConfig={setNodeConfig}
              currentNodeId={selectedNode.id}
              allNodes={nodes}
              allEdges={edges}
              workflowTrigger={workflow.workflowTrigger}
            />
          )}
        </ModalBody>
        <ModalFooter>
          {selectedNode && selectedNode.type === 'action' && nodeConfig.type && (
            <Button 
              color="info" 
              onClick={handleTestStep}
              disabled={testing}
            >
              <i className="ri-play-line me-1"></i>
              {testing ? "Probando..." : "Probar Paso"}
            </Button>
          )}
          <Button color="success" onClick={handleSaveNodeConfig}>
            Guardar Configuración
          </Button>
          <Button color="danger" onClick={() => selectedNode && handleDeleteNode(selectedNode.id)}>
            Eliminar Paso
          </Button>
          <Button color="secondary" onClick={() => {
            setConfigModal(false);
            setTestResult(null);
          }}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

// Helper function to get available variables from previous nodes
// This function adds dynamic variables from previous steps to the API variables
const getAvailableVariables = (currentNodeId, allNodes, allEdges, workflowTrigger) => {
  // Start with empty object - contact variables come from API
  const variables = {};

  // Add event variables if trigger is contact_updated
  // workflowTrigger can be null/undefined, so we use optional chaining
  if (workflowTrigger && workflowTrigger.type === 'contact_updated') {
    variables.event = [
      { label: 'Evento - Objeto Anterior (old)', value: 'event.old', type: 'object' },
      { label: 'Evento - Objeto Nuevo (new)', value: 'event.new', type: 'object' },
      { label: 'Evento - Campos Cambiados', value: 'event.changedFields', type: 'array' },
      { label: 'Evento - Email Anterior', value: 'event.old.email', type: 'string' },
      { label: 'Evento - Email Nuevo', value: 'event.new.email', type: 'string' },
      { label: 'Evento - Nombre Anterior', value: 'event.old.name', type: 'string' },
      { label: 'Evento - Nombre Nuevo', value: 'event.new.name', type: 'string' },
      { label: 'Evento - Teléfono Anterior', value: 'event.old.phone', type: 'string' },
      { label: 'Evento - Teléfono Nuevo', value: 'event.new.phone', type: 'string' },
    ];
  }

  // Find all nodes that come before the current node
  const previousNodes = [];
  const visited = new Set();
  
  const findPreviousNodes = (nodeId) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    
    const incomingEdges = allEdges.filter(e => e.target === nodeId);
    incomingEdges.forEach(edge => {
      const sourceNode = allNodes.find(n => n.id === edge.source);
      if (sourceNode && sourceNode.id !== nodeId) {
        previousNodes.push(sourceNode);
        findPreviousNodes(sourceNode.id);
      }
    });
  };
  
  findPreviousNodes(currentNodeId);
  
  // Add variables from previous nodes
  previousNodes.forEach(node => {
    const nodeData = node.data?.step;
    if (!nodeData) return;
    
    switch (nodeData.type) {
      case 'send_email':
      case 'send_email_html':
        variables[`previousResults.step-${node.id}`] = [
          { label: `${node.data.label} - Email Enviado`, value: `previousResults.step-${node.id}.sent`, type: 'boolean' },
          { label: `${node.data.label} - Message ID`, value: `previousResults.step-${node.id}.messageId`, type: 'string' },
          { label: `${node.data.label} - Canal`, value: `previousResults.step-${node.id}.channel`, type: 'string' },
        ];
        // Legacy format for backward compatibility
        variables[`step_${node.id}`] = [
          { label: `${node.data.label} - Email Enviado`, value: `step_${node.id}.sent`, type: 'boolean' },
          { label: `${node.data.label} - Message ID`, value: `step_${node.id}.messageId`, type: 'string' },
        ];
        break;
      case 'send_whatsapp':
        variables[`step_${node.id}`] = [
          { label: `${node.data.label} - WhatsApp Enviado`, value: `step_${node.id}.sent`, type: 'boolean' },
          { label: `${node.data.label} - WhatsApp Entregado`, value: `step_${node.id}.delivered`, type: 'boolean' },
          { label: `${node.data.label} - WhatsApp Leído`, value: `step_${node.id}.read`, type: 'boolean' },
        ];
        break;
      case 'webhook':
        variables[`previousResults.step-${node.id}`] = [
          { label: `${node.data.label} - Status HTTP`, value: `previousResults.step-${node.id}.status`, type: 'number' },
          { label: `${node.data.label} - Response Data`, value: `previousResults.step-${node.id}.responseData`, type: 'object' },
          { label: `${node.data.label} - Éxito`, value: `previousResults.step-${node.id}.called`, type: 'boolean' },
        ];
        // Legacy format
        variables[`step_${node.id}`] = [
          { label: `${node.data.label} - Respuesta HTTP`, value: `step_${node.id}.response.status`, type: 'number' },
          { label: `${node.data.label} - Respuesta Body`, value: `step_${node.id}.response.body`, type: 'object' },
          { label: `${node.data.label} - Éxito`, value: `step_${node.id}.success`, type: 'boolean' },
        ];
        break;
      case 'update_field':
        variables[`step_${node.id}`] = [
          { label: `${node.data.label} - Campo Actualizado`, value: `step_${node.id}.field`, type: 'string' },
          { label: `${node.data.label} - Valor Anterior`, value: `step_${node.id}.oldValue`, type: 'string' },
          { label: `${node.data.label} - Valor Nuevo`, value: `step_${node.id}.newValue`, type: 'string' },
        ];
        break;
      case 'add_to_list':
      case 'remove_from_list':
        variables[`step_${node.id}`] = [
          { label: `${node.data.label} - Lista ID`, value: `step_${node.id}.listId`, type: 'string' },
          { label: `${node.data.label} - Agregado`, value: `step_${node.id}.added`, type: 'boolean' },
        ];
        break;
    }
  });
  
  return variables;
};

// Node Configuration Form Component
const NodeConfigForm = ({ nodeType, config, setConfig, currentNodeId, allNodes, allEdges, workflowTrigger }) => {
  const [contactLists, setContactLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  // Líneas de WhatsApp de la empresa. Hacen falta cuando hay más de una: el
  // motor elegía una arbitraria y el paso no podía decir cuál.
  const [whatsappLineas, setWhatsappLineas] = useState([]);
  const [emailProviders, setEmailProviders] = useState([]);
  const [salesTeams, setSalesTeams] = useState([]);
  const { variables: apiVariables, loading: variablesLoading } = useWorkflowVariables();
  
  // Debug: Log when variables are loaded
  useEffect(() => {
    if (apiVariables) {
      console.log('NodeConfigForm: Variables loaded', {
        hasContactFields: !!apiVariables.contactFields,
        variableCount: apiVariables.contactFields?.variables ? Object.keys(apiVariables.contactFields.variables).length : 0,
        variables: apiVariables
      });
    }
  }, [apiVariables]);

  useEffect(() => {
    if (nodeType === 'action') {
      if (config.type === 'add_to_list' || config.type === 'remove_from_list') {
        GetData('/contact-lists').then((res) => {
          setContactLists(res?.data || []);
        }).catch(err => console.error('Error loading contact lists:', err));
      }
      if (config.type === 'send_email' || config.type === 'send_email_html') {
        Promise.all([
          GetData('/templates/email').then(res => Array.isArray(res?.data) ? res.data : []).catch(() => []),
          GetData('/email/providers').then(res => Array.isArray(res?.data) ? res.data : []).catch(() => [])
        ]).then(([templatesData, providersData]) => {
          setTemplates(Array.isArray(templatesData) ? templatesData : []);
          setEmailProviders(Array.isArray(providersData) ? providersData : []);
        });
      }
      if (config.type === 'send_whatsapp') {
        GetData('/phones')
          .then((res) => setWhatsappLineas(Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : [])))
          .catch(() => setWhatsappLineas([]));
        GetData('/templates/whatsapp').then((res) => {
          setWhatsappTemplates(res?.data || res || []);
        }).catch(err => {
          console.error('Error loading WhatsApp templates:', err);
          setWhatsappTemplates([]);
        });
      }
      if (config.type === 'assign_to_sales_rep') {
        Promise.all([
          GetData('/sales-teams').then(res => Array.isArray(res?.data) ? res.data : []).catch(() => []),
          GetData('/email/providers').then(res => Array.isArray(res?.data) ? res.data : []).catch(() => [])
        ]).then(([salesTeamsData, providersData]) => {
          setSalesTeams(Array.isArray(salesTeamsData) ? salesTeamsData : []);
          setEmailProviders(Array.isArray(providersData) ? providersData : []);
        });
      }
    }
  }, [nodeType, config.type]);

  const renderConfig = () => {
    switch (config.type) {
      case 'send_email':
        return (
          <>
            <FormGroup>
              <Label>Proveedor SMTP *</Label>
              <Input
                type="select"
                value={config.config?.emailProviderId || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, emailProviderId: e.target.value }
                })}
                required
              >
                <option value="">Seleccionar proveedor</option>
                {Array.isArray(emailProviders) && emailProviders.map(provider => (
                  <option key={provider.key} value={provider.key}>
                    {provider.displayName} {provider.status === 'configured' ? '✓' : ''}
                  </option>
                ))}
              </Input>
              <small className="text-muted">
                Selecciona el proveedor SMTP configurado (Mailgun, SES, etc.)
              </small>
            </FormGroup>
            <FormGroup>
              <Label>Email Remitente (From)</Label>
              <Input
                type="email"
                value={config.config?.fromEmail || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, fromEmail: e.target.value }
                })}
                placeholder="noreply@tudominio.com"
              />
              <small className="text-muted">
                Opcional. Si no se especifica, se usará el email del proveedor configurado
              </small>
            </FormGroup>
            <FormGroup>
              <Label>Email Destinatario (To)</Label>
              <VariableAutocomplete
                value={config.config?.to || ''}
                onChange={(newValue) => setConfig({
                  ...config,
                  config: { ...config.config, to: newValue }
                })}
                availableVariables={{
                  contactFields: apiVariables?.contactFields || {},
                  ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger)
                }}
                placeholder="{{email}} o email@ejemplo.com"
              />
              <small className="text-muted">
                Opcional. Usa variables como {'{{email}}'} o escribe un email manualmente. Si no se especifica, se usará el email del contacto.
              </small>
            </FormGroup>
            <FormGroup>
              <Label>Plantilla *</Label>
              <Input
                type="select"
                value={config.config?.templateId || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, templateId: e.target.value }
                })}
                required
              >
                <option value="">Seleccionar plantilla</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Input>
              {templates.length === 0 && (
                <small className="text-warning">
                  No hay plantillas disponibles. Crea una en el módulo de Plantillas.
                </small>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Asunto *</Label>
              <VariableAutocomplete
                value={config.config?.subject || ''}
                onChange={(newValue) => setConfig({
                  ...config,
                  config: { ...config.config, subject: newValue }
                })}
                availableVariables={{
                  contactFields: apiVariables?.contactFields || {},
                  ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger || { type: 'contact_created' })
                }}
                placeholder="Asunto del email (usa {{ para variables)"
                required
              />
            </FormGroup>
          </>
        );

      case 'send_email_html':
        return (
          <>
            <FormGroup>
              <Label>Proveedor SMTP *</Label>
              <Input
                type="select"
                value={config.config?.emailProviderId || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, emailProviderId: e.target.value }
                })}
                required
              >
                <option value="">Seleccionar proveedor</option>
                {Array.isArray(emailProviders) && emailProviders.map(provider => (
                  <option key={provider.key} value={provider.key}>
                    {provider.displayName} {provider.status === 'configured' ? '✓' : ''}
                  </option>
                ))}
              </Input>
              <small className="text-muted">
                Selecciona el proveedor SMTP configurado (Mailgun, SES, etc.)
              </small>
            </FormGroup>
            <FormGroup>
              <Label>Email Remitente (From)</Label>
              <Input
                type="email"
                value={config.config?.fromEmail || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, fromEmail: e.target.value }
                })}
                placeholder="noreply@tudominio.com"
              />
              <small className="text-muted">
                Opcional. Si no se especifica, se usará el email del proveedor configurado
              </small>
            </FormGroup>
            <FormGroup>
              <Label>Email Destinatario (To)</Label>
              <VariableAutocomplete
                value={config.config?.to || ''}
                onChange={(newValue) => setConfig({
                  ...config,
                  config: { ...config.config, to: newValue }
                })}
                availableVariables={{
                  contactFields: apiVariables?.contactFields || {},
                  ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger)
                }}
                placeholder="{{email}} o email@ejemplo.com"
              />
              <small className="text-muted">
                Opcional. Usa variables como {'{{email}}'} o escribe un email manualmente. Si no se especifica, se usará el email del contacto.
              </small>
            </FormGroup>
            <FormGroup>
              <Label>Asunto *</Label>
              <VariableAutocomplete
                value={config.config?.subject || ''}
                onChange={(newValue) => setConfig({
                  ...config,
                  config: { ...config.config, subject: newValue }
                })}
                availableVariables={{
                  contactFields: apiVariables?.contactFields || {},
                  ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger)
                }}
                placeholder="Asunto del email (usa {{ para variables)"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Contenido HTML *</Label>
              <VariableAutocomplete
                value={config.config?.htmlContent || ''}
                onChange={(newValue) => setConfig({
                  ...config,
                  config: { ...config.config, htmlContent: newValue }
                })}
                availableVariables={{
                  contactFields: apiVariables?.contactFields || {},
                  ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger)
                }}
                placeholder='<html><body><h1>Hola {{name}}</h1><p>Tu email es {{email}}</p></body></html>'
                rows={12}
                required
              />
            </FormGroup>
          </>
        );

      case 'send_whatsapp': {
        const CONTACT_TOKENS = [
          { value: '{{contactName}}', label: 'Nombre del contacto' },
          { value: '{{contactPhone}}', label: 'Teléfono' },
          { value: '{{contactEmail}}', label: 'Correo' },
          { value: '{{company}}', label: 'Empresa' },
          { value: '{{createdBy}}', label: 'Campaña / origen' },
          { value: '__FIXED__', label: 'Valor fijo…' },
        ];
        const KNOWN = CONTACT_TOKENS.filter(t => t.value !== '__FIXED__').map(t => t.value);
        const bodyOf = (tpl) => ((tpl?.params?.components || []).find(c => String(c.type).toUpperCase() === 'BODY') || {}).text || '';
        const varsInText = (txt) => [...new Set((txt.match(/\{\{\s*(\d+)\s*\}\}/g) || []).map(m => m.replace(/\D/g, '')))].sort((a, b) => Number(a) - Number(b));

        // La línea elegida manda. Las plantillas de WhatsApp se aprueban POR
        // CUENTA (WABA), así que una plantilla de una cuenta no existe en la
        // otra: Meta la rechaza con "template name does not exist".
        const lineaId = config.config?.phoneNumberId || '';
        const lineaSel = whatsappLineas.find(l => String(l.numberId || l.id) === String(lineaId));
        const etiquetaLinea = (l) =>
          [l.label, l.number].filter(Boolean).join(' · ') || String(l.numberId || l.id);

        // Si la línea tiene WABA, se ocultan las plantillas de otras cuentas.
        // Las plantillas SIN waba_id no se pueden atribuir, así que se dejan
        // visibles: son las importadas antes de que se guardara ese dato, y
        // esconderlas dejaría el desplegable vacío sin explicación.
        const plantillasVisibles = (!lineaSel || !lineaSel.wabaId)
          ? whatsappTemplates
          : whatsappTemplates.filter(t => !t.waba_id || String(t.waba_id) === String(lineaSel.wabaId));

        const selectedTpl = plantillasVisibles.find(t => String(t.id) === String(config.config?.whatsappTemplateId));
        const bodyText = bodyOf(selectedTpl);
        const varNums = varsInText(bodyText);
        const variables = config.config?.variables || {};
        const setVar = (n, val) => setConfig({ ...config, config: { ...config.config, variables: { ...variables, [n]: val } } });

        return (
          <>
            {whatsappLineas.length > 1 && (
              <FormGroup>
                <Label>Línea de WhatsApp *</Label>
                <Input
                  type="select"
                  value={lineaId}
                  onChange={(e) => {
                    // Al cambiar de línea se limpia la plantilla: la anterior
                    // puede ser de otra cuenta y no existir en esta.
                    setConfig({
                      ...config,
                      config: { ...config.config, phoneNumberId: e.target.value, whatsappTemplateId: '', variables: {} },
                    });
                  }}
                  required
                >
                  <option value="">Seleccionar línea</option>
                  {whatsappLineas.map(l => (
                    <option key={l.numberId || l.id} value={l.numberId || l.id}>
                      {etiquetaLinea(l)}
                    </option>
                  ))}
                </Input>
                <small className="text-muted">
                  Esta empresa tiene {whatsappLineas.length} líneas. Sin elegir una, el envío sale por cualquiera.
                </small>
              </FormGroup>
            )}

            <FormGroup>
              <Label>Plantilla de WhatsApp *</Label>
              <Input
                type="select"
                value={config.config?.whatsappTemplateId || ''}
                onChange={(e) => {
                  const tid = e.target.value;
                  const tpl = whatsappTemplates.find(t => String(t.id) === String(tid));
                  const vn = varsInText(bodyOf(tpl));
                  const vars = {};
                  vn.forEach(n => { vars[n] = (config.config?.variables?.[n]) || '{{contactName}}'; });
                  setConfig({ ...config, config: { ...config.config, whatsappTemplateId: tid, variables: vars } });
                }}
                required
              >
                <option value="">Seleccionar plantilla</option>
                {plantillasVisibles.map(template => (
                  <option key={template.id} value={template.id} disabled={String(template.status || '').toUpperCase() !== 'APPROVED'}>
                    {template.name} {template.status ? `(${template.status})` : ''}
                  </option>
                ))}
              </Input>
              {plantillasVisibles.length === 0 && (
                <div className="alert alert-warning mt-2" style={{ fontSize: '12px', padding: '8px' }}>
                  <i className="ri-alert-line me-1"></i>
                  No hay plantillas de WhatsApp disponibles. Crea una en Plantillas de WhatsApp.
                </div>
              )}
              <small className="text-muted">Solo se pueden usar plantillas <b>aprobadas</b>.</small>
            </FormGroup>

            {selectedTpl && (
              <FormGroup>
                <Label>Contenido de la plantilla</Label>
                <div className="border rounded p-2 bg-light" style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                  {bodyText || <span className="text-muted">(sin cuerpo)</span>}
                </div>
              </FormGroup>
            )}

            {selectedTpl && varNums.length > 0 && (
              <FormGroup>
                <Label>Variables → dato del contacto</Label>
                {varNums.map((n) => {
                  const cur = variables[n] ?? '{{contactName}}';
                  const isFixed = !KNOWN.includes(cur);
                  return (
                    <div key={n} className="d-flex gap-2 align-items-center mb-2">
                      <span className="badge bg-success" style={{ width: '46px', flex: '0 0 auto' }}>{`{{${n}}}`}</span>
                      <Input
                        type="select"
                        bsSize="sm"
                        style={{ maxWidth: '220px', flex: '0 0 auto' }}
                        value={isFixed ? '__FIXED__' : cur}
                        onChange={(e) => setVar(n, e.target.value === '__FIXED__' ? '' : e.target.value)}
                      >
                        {CONTACT_TOKENS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </Input>
                      {isFixed && (
                        <Input bsSize="sm" value={cur} placeholder="Texto fijo" onChange={(e) => setVar(n, e.target.value)} />
                      )}
                    </div>
                  );
                })}
                <small className="text-muted">Cada variable se reemplaza al enviar con el dato del contacto (o el texto fijo).</small>
              </FormGroup>
            )}
          </>
        );
      }

      case 'webhook':
        const webhookSendFullObject = config.config?.webhookSendFullObject || false;
        return (
          <>
            <FormGroup>
              <Label>URL del Webhook *</Label>
              <Input
                type="url"
                value={config.config?.webhookUrl || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, webhookUrl: e.target.value }
                })}
                placeholder="https://api.ejemplo.com/webhook"
                required
              />
              <small className="text-muted">
                URL completa del endpoint que recibirá la petición
              </small>
            </FormGroup>
            <FormGroup>
              <Label>Método HTTP *</Label>
              <Input
                type="select"
                value={config.config?.webhookMethod || 'POST'}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, webhookMethod: e.target.value }
                })}
                required
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
                <option value="PATCH">PATCH</option>
              </Input>
            </FormGroup>
            {!['GET', 'DELETE'].includes(config.config?.webhookMethod || 'POST') && (
              <>
                <FormGroup>
                  <Label>Modo de Envío *</Label>
                  <Input
                    type="select"
                    value={webhookSendFullObject ? 'full' : 'custom'}
                    onChange={(e) => {
                      const sendFull = e.target.value === 'full';
                      setConfig({
                        ...config,
                        config: { 
                          ...config.config, 
                          webhookSendFullObject: sendFull,
                          // Si cambia a full, limpiar el body personalizado
                          webhookBody: sendFull ? '' : (config.config?.webhookBody || '')
                        }
                      });
                    }}
                    required
                  >
                    <option value="custom">Objeto Personalizado (usar variables)</option>
                    <option value="full">Enviar Objeto Completo del Contacto</option>
                  </Input>
                  <small className="text-muted">
                    {webhookSendFullObject 
                      ? 'Se enviará todo el objeto del contacto automáticamente. No necesitas configurar el body.'
                      : 'Crea tu propio objeto usando variables disponibles.'}
                  </small>
                </FormGroup>
                {!webhookSendFullObject && (
                  <>
                    <FormGroup>
                      <Label>Tipo de Body *</Label>
                      <Input
                        type="select"
                        value={config.config?.webhookBodyType || 'json'}
                        onChange={(e) => setConfig({
                          ...config,
                          config: { ...config.config, webhookBodyType: e.target.value }
                        })}
                        required
                      >
                        <option value="json">JSON</option>
                        <option value="form-data">Form Data (application/x-www-form-urlencoded)</option>
                        <option value="raw">Raw (text/plain)</option>
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label>Body *</Label>
                      <VariableAutocomplete
                        value={config.config?.webhookBody || ''}
                        onChange={(newValue) => setConfig({
                          ...config,
                          config: { ...config.config, webhookBody: newValue }
                        })}
                        availableVariables={{
                          ...(apiVariables?.contactFields || {}),
                          ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger)
                        }}
                        placeholder={
                          config.config?.webhookBodyType === 'json' 
                            ? '{"name": "{{name}}", "email": "{{email}}", "phone": "{{phone}}", "city": "{{city}}"}'
                            : config.config?.webhookBodyType === 'form-data'
                            ? 'name={{name}}&email={{email}}&phone={{phone}}&city={{city}}'
                            : 'Texto plano con variables {{name}}, {{email}}, {{phone}}'
                        }
                        rows={8}
                        required
                      />
                      <small className="text-muted mt-1 d-block">
                        <i className="ri-information-line me-1"></i>
                        Usa <code>{'{{'}</code> para ver todas las variables disponibles del contacto y pasos anteriores.
                      </small>
                    </FormGroup>
                  </>
                )}
                {webhookSendFullObject && (
                  <div className="alert alert-info" style={{ fontSize: '12px', padding: '12px' }}>
                    <i className="ri-information-line me-2"></i>
                    <strong>Modo: Enviar Objeto Completo</strong>
                    <p className="mb-0 mt-2">
                      Se enviará automáticamente todo el objeto del contacto con la siguiente estructura:
                    </p>
                    <pre style={{ fontSize: '11px', marginTop: '8px', background: '#f8f9fa', padding: '8px', borderRadius: '4px' }}>
{`{
  "id": "...",
  "name": "...",
  "email": "...",
  "phone": "...",
  "city": "...",
  "address": "...",
  "state": "...",
  "postalCode": "...",
  "country": "...",
  "type": "...",
  "url": "...",
  "tags": [...],
  "referencia": "...",
  "companyId": "...",
  "createdBy": "...",
  "createdAt": "...",
  "updatedAt": "...",
  "attributes": {...},
  // ... todos los campos del contacto
}`}
                    </pre>
                  </div>
                )}
              </>
            )}
            {['GET', 'DELETE'].includes(config.config?.webhookMethod || 'POST') && (
              <div className="alert alert-info" style={{ fontSize: '12px', padding: '8px' }}>
                <i className="ri-information-line me-1"></i>
                Los métodos GET y DELETE no requieren body. Las variables se pueden usar en la URL.
              </div>
            )}
          </>
        );

      case 'add_to_list':
      case 'remove_from_list':
        return (
          <>
            <FormGroup>
              <Label>Lista de Contactos *</Label>
              <Input
                type="select"
                value={config.config?.listId || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, listId: e.target.value }
                })}
                required
              >
                <option value="">Seleccionar lista</option>
                {contactLists.map(list => (
                  <option key={list.id} value={list.id}>
                    {list.name} {list.type === 'dynamic' ? '(Dinámica)' : '(Estática)'}
                  </option>
                ))}
              </Input>
              {contactLists.length === 0 && (
                <div className="alert alert-warning mt-2" style={{ fontSize: '12px', padding: '8px' }}>
                  <i className="ri-alert-line me-1"></i>
                  No hay listas disponibles. Crea una en el módulo de Listas de Contactos.
                </div>
              )}
              <small className="text-muted">
                {config.type === 'add_to_list' 
                  ? 'El contacto será agregado a esta lista si no está ya incluido'
                  : 'El contacto será removido de esta lista si está incluido'}
              </small>
            </FormGroup>
          </>
        );

      case 'update_field':
        return (
          <>
            <FormGroup>
              <Label>Campo a Actualizar</Label>
              <Input
                type="select"
                value={config.config?.field || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, field: e.target.value }
                })}
              >
                <option value="">Seleccionar campo</option>
                <optgroup label="Información Básica">
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="email">Email</option>
                  <option value="phone">Teléfono</option>
                  <option value="type">Tipo</option>
                </optgroup>
                <optgroup label="Ubicación">
                  <option value="city">Ciudad</option>
                  <option value="state">Estado</option>
                  <option value="country">País</option>
                  <option value="address">Dirección</option>
                  <option value="postalCode">Código Postal</option>
                </optgroup>
                <optgroup label="Atributos Personalizados">
                  <option value="attributes">Atributos (JSON)</option>
                </optgroup>
              </Input>
              <small className="text-muted">
                Selecciona el campo del contacto que deseas actualizar
              </small>
            </FormGroup>
            <FormGroup>
              <Label>Nuevo Valor</Label>
              <VariableAutocomplete
                value={config.config?.value || ''}
                onChange={(newValue) => setConfig({
                  ...config,
                  config: { ...config.config, value: newValue }
                })}
                availableVariables={{
                  contactFields: apiVariables?.contactFields || {},
                  ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger || { type: 'contact_created' })
                }}
                placeholder="Ingresa el nuevo valor o usa variables como {{contactName}}"
              />
            </FormGroup>
          </>
        );

      case 'assign_to_sales_rep':
        return (
          <>
            <FormGroup>
              <Label>Equipo de Ventas</Label>
              <Input
                type="select"
                value={config.config?.salesTeamId || ''}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, salesTeamId: e.target.value }
                })}
              >
                <option value="">Asignación Automática (Round Robin)</option>
                {salesTeams
                  .filter(team => team.isActive !== false)
                  .map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name} {team.assignmentStrategy === 'percentage' ? '(Por Porcentajes)' : '(Round Robin)'}
                    </option>
                  ))}
              </Input>
              <small className="text-muted">
                Selecciona un equipo específico o deja en blanco para asignación automática basada en round-robin.
                Si seleccionas un equipo, se asignará al siguiente miembro disponible según la estrategia del equipo.
              </small>
            </FormGroup>
            <FormGroup check className="mt-3">
              <Input
                type="checkbox"
                id="send-email-on-assignment"
                checked={config.config?.sendEmailToAssignedMember || false}
                onChange={(e) => {
                  e.stopPropagation();
                  console.log(`[WorkflowBuilder] Checkbox onChange for sendEmailToAssignedMember, checked: ${e.target.checked}`);
                  setConfig({
                    ...config,
                    config: { ...config.config, sendEmailToAssignedMember: e.target.checked }
                  });
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log(`[WorkflowBuilder] Checkbox onClick for sendEmailToAssignedMember`);
                }}
              />
              <Label 
                check 
                htmlFor="send-email-on-assignment"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(`[WorkflowBuilder] Label onClick for sendEmailToAssignedMember`);
                  setConfig({
                    ...config,
                    config: { ...config.config, sendEmailToAssignedMember: !(config.config?.sendEmailToAssignedMember || false) }
                  });
                }}
                style={{ cursor: 'pointer' }}
              >
                Enviar correo al miembro asignado
              </Label>
              <small className="text-muted d-block mt-1">
                Si está marcado, se enviará un correo de notificación al miembro del equipo cuando se le asigne un lead.
              </small>
            </FormGroup>
            
            {config.config?.sendEmailToAssignedMember && (
              <>
                <FormGroup className="mt-3">
                  <Label>Proveedor SMTP *</Label>
                  <Input
                    type="select"
                    value={config.config?.emailProviderId || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      config: { ...config.config, emailProviderId: e.target.value }
                    })}
                    required
                  >
                    <option value="">Seleccionar proveedor</option>
                    {Array.isArray(emailProviders) && emailProviders.map(provider => (
                      <option key={provider.key} value={provider.key}>
                        {provider.displayName} {provider.status === 'configured' ? '✓' : ''}
                      </option>
                    ))}
                  </Input>
                  <small className="text-muted">
                    Selecciona el proveedor SMTP configurado (Mailgun, SES, etc.)
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label>Email Remitente (From)</Label>
                  <Input
                    type="email"
                    value={config.config?.fromEmail || ''}
                    onChange={(e) => setConfig({
                      ...config,
                      config: { ...config.config, fromEmail: e.target.value }
                    })}
                    placeholder="noreply@tudominio.com"
                  />
                  <small className="text-muted">
                    Opcional. Si no se especifica, se usará el email del proveedor configurado
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label>Asunto del Email *</Label>
                  <VariableAutocomplete
                    value={config.config?.emailSubject || 'Nuevo Lead Asignado: {{customerName}}'}
                    onChange={(newValue) => setConfig({
                      ...config,
                      config: { ...config.config, emailSubject: newValue }
                    })}
                    availableVariables={{
                      contactFields: apiVariables?.contactFields || {},
                      ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger)
                    }}
                    placeholder="Nuevo Lead Asignado: {{customerName}}"
                    required
                  />
                  <small className="text-muted">
                    Usa variables como {'{{customerName}}'}, {'{{customerEmail}}'}, etc.
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label>Contenido HTML del Email *</Label>
                  <VariableAutocomplete
                    value={config.config?.emailHtmlContent || ''}
                    onChange={(newValue) => setConfig({
                      ...config,
                      config: { ...config.config, emailHtmlContent: newValue }
                    })}
                    availableVariables={{
                      contactFields: apiVariables?.contactFields || {},
                      ...getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger)
                    }}
                    placeholder='<html><body><h2>Nuevo Lead Asignado</h2><p>Hola {{userName}},</p><p>Se te ha asignado un nuevo lead: {{customerName}}</p></body></html>'
                    rows={12}
                    required
                  />
                  <small className="text-muted">
                    Contenido HTML del email. Usa variables como {'{{userName}}'}, {'{{customerName}}'}, {'{{customerEmail}}'}, {'{{customerPhone}}'}, etc.
                  </small>
                </FormGroup>
              </>
            )}
          </>
        );

      case 'wait':
        return (
          <FormGroup>
            <Label>Tiempo de Espera (minutos)</Label>
            <Input
              type="number"
              min="0"
              value={config.config?.delay || 0}
              onChange={(e) => setConfig({
                ...config,
                config: { ...config.config, delay: parseInt(e.target.value) || 0 }
              })}
            />
          </FormGroup>
        );

      case 'condition':
        // For conditions, only use conditionFields from API, not all contactFields
        const conditionFields = apiVariables?.conditionFields?.variables || {};
        // Convert conditionFields to the format expected by the select dropdown
        const conditionVarsList = Object.keys(conditionFields).map(key => ({
          value: key,
          label: conditionFields[key].description || key,
          type: 'string'
        }));
        
        // Get variables from previous steps (for step results)
        const stepVars = getAvailableVariables(currentNodeId, allNodes, allEdges, workflowTrigger);
        
        // Combine condition fields with step variables
        const availableVars = {
          condition: conditionVarsList,
          ...stepVars
        };
        
        const conditions = config.config?.conditions || [];
        
        return (
          <div>
            <FormGroup>
              <Label>Lógica de Condiciones</Label>
              <Input
                type="select"
                value={config.config?.logic || 'AND'}
                onChange={(e) => setConfig({
                  ...config,
                  config: { ...config.config, logic: e.target.value }
                })}
              >
                <option value="AND">Y (AND) - Todas deben cumplirse</option>
                <option value="OR">O (OR) - Al menos una debe cumplirse</option>
              </Input>
            </FormGroup>
            
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <Label className="mb-0">Condiciones</Label>
                  <small className="text-muted d-block">
                    Solo puedes usar los campos de comparación disponibles
                  </small>
                </div>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => {
                    const newCondition = {
                      field: '',
                      operator: 'equals',
                      value: ''
                    };
                    setConfig({
                      ...config,
                      config: {
                        ...config.config,
                        conditions: [...conditions, newCondition]
                      }
                    });
                  }}
                >
                  <i className="ri-add-line me-1"></i>Agregar Condición
                </Button>
              </div>
              
              {conditionVarsList.length > 0 && (
                <div className="alert alert-info mb-3" style={{ fontSize: '12px' }}>
                  <strong>Campos disponibles para comparación:</strong>
                  <ul className="mb-0 mt-2" style={{ paddingLeft: '20px' }}>
                    {conditionVarsList.map(v => (
                      <li key={v.value}>
                        <strong>{v.value}:</strong> {v.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {conditions.length === 0 ? (
                <div className="text-center p-4 border rounded">
                  <i className="ri-information-line text-muted" style={{ fontSize: '32px' }}></i>
                  <p className="text-muted mt-2 mb-0">No hay condiciones. Agrega una para comenzar.</p>
                  <small className="text-muted">Ejemplo: Verificar si referencia contiene "12345", si tags contiene "vip", etc.</small>
                </div>
              ) : (
                conditions.map((condition, index) => (
                  <Card key={index} className="mb-2">
                    <CardBody>
                      <div className="d-flex gap-2 align-items-end">
                        <FormGroup style={{ flex: 1 }}>
                          <Label>Campo/Variable</Label>
                          <div className="d-flex gap-2">
                            <Input
                              type="select"
                              value={condition.field || ''}
                              onChange={(e) => {
                                const updated = [...conditions];
                                updated[index] = { ...updated[index], field: e.target.value, isCustom: false };
                                setConfig({
                                  ...config,
                                  config: { ...config.config, conditions: updated }
                                });
                              }}
                              style={{ flex: 1 }}
                            >
                              <option value="">Seleccionar campo de comparación...</option>
                              {conditionVarsList.length > 0 && (
                                <optgroup label="📋 Campos de Comparación">
                                  {conditionVarsList.map(v => (
                                    <option key={v.value} value={v.value}>
                                      {v.label}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                              {Object.keys(stepVars).length > 0 && Object.keys(stepVars).map(group => (
                                <optgroup key={group} label={`🔗 ${group.replace('step_', 'Nodo ')}`}>
                                  {stepVars[group].map(v => (
                                    <option key={v.value} value={v.value}>
                                      {v.label} ({v.type})
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                              <optgroup label="🔄 Evento de Actualización">
                                <option value="event.old">Objeto Anterior (old)</option>
                                <option value="event.new">Objeto Nuevo (new)</option>
                                <option value="event.changedFields">Campos Cambiados</option>
                              </optgroup>
                            </Input>
                            <Button
                              color={condition.isCustom ? "primary" : "secondary"}
                              size="sm"
                              onClick={() => {
                                const updated = [...conditions];
                                updated[index] = { 
                                  ...updated[index], 
                                  field: condition.isCustom ? '' : condition.field || '',
                                  isCustom: !condition.isCustom 
                                };
                                setConfig({
                                  ...config,
                                  config: { ...config.config, conditions: updated }
                                });
                              }}
                              title={condition.isCustom ? "Usar selector de variables" : "Usar campo personalizado"}
                            >
                              <i className={condition.isCustom ? "ri-list-check" : "ri-edit-line"}></i>
                            </Button>
                          </div>
                          {condition.isCustom ? (
                            <div className="mt-2">
                              <Input
                                type="text"
                                value={condition.field || ''}
                                onChange={(e) => {
                                  const updated = [...conditions];
                                  updated[index] = { ...updated[index], field: e.target.value };
                                  setConfig({
                                    ...config,
                                    config: { ...config.config, conditions: updated }
                                  });
                                }}
                                placeholder="Ej: contact.attributes.customField, step_123.response.data.id, event.old.email"
                              />
                              <small className="text-muted d-block mt-1">
                                Escribe la ruta completa del campo usando notación de punto (ej: contact.attributes.miCampo)
                              </small>
                            </div>
                          ) : condition.field && (
                            <small className="text-muted d-block mt-1">
                              Variable seleccionada: <code>{condition.field}</code>
                            </small>
                          )}
                          <small className="text-muted d-block mt-1">
                            <strong>Ejemplos:</strong> contact.name, contact.attributes.customField, step_123.response.status, event.old.email, event.new.phone
                          </small>
                        </FormGroup>
                        
                        <FormGroup style={{ width: '150px' }}>
                          <Label>Operador</Label>
                          <Input
                            type="select"
                            value={condition.operator || 'equals'}
                            onChange={(e) => {
                              const updated = [...conditions];
                              updated[index] = { ...updated[index], operator: e.target.value };
                              setConfig({
                                ...config,
                                config: { ...config.config, conditions: updated }
                              });
                            }}
                          >
                            <option value="equals">Igual a</option>
                            <option value="not_equals">No igual a</option>
                            <option value="contains">Contiene</option>
                            <option value="not_contains">No contiene</option>
                            <option value="greater_than">Mayor que</option>
                            <option value="less_than">Menor que</option>
                            <option value="is_empty">Está vacío</option>
                            <option value="is_not_empty">No está vacío</option>
                          </Input>
                        </FormGroup>
                        
                        {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                          <FormGroup style={{ flex: 1 }}>
                            <Label>Valor</Label>
                            <Input
                              type="text"
                              value={condition.value || ''}
                              onChange={(e) => {
                                const updated = [...conditions];
                                updated[index] = { ...updated[index], value: e.target.value };
                                setConfig({
                                  ...config,
                                  config: { ...config.config, conditions: updated }
                                });
                              }}
                              placeholder="Valor a comparar"
                            />
                          </FormGroup>
                        )}
                        
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => {
                            const updated = conditions.filter((_, i) => i !== index);
                            setConfig({
                              ...config,
                              config: { ...config.config, conditions: updated }
                            });
                          }}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                ))
              )}
            </div>
          </div>
        );

      default:
        return <p>Selecciona un tipo de acción</p>;
    }
  };

  if (nodeType === 'trigger') {
    return <p className="text-muted">El trigger se configura en la parte superior del formulario.</p>;
  }

  if (nodeType === 'action' && (!config.type || config.type === '')) {
    return (
      <>
        <div className="alert alert-info mb-3">
          <i className="ri-information-line me-2"></i>
          <strong>Selecciona el tipo de acción</strong> que deseas configurar para este paso del workflow.
        </div>
        <FormGroup>
          <Label>Tipo de Acción *</Label>
          <Input
            type="select"
            value={config.type || ''}
            onChange={(e) => {
              const newType = e.target.value;
              if (newType) {
                setConfig({ 
                  ...config, 
                  type: newType, 
                  config: getDefaultConfigForType(newType) 
                });
              }
            }}
            required
          >
            <option value="">-- Seleccionar tipo de acción --</option>
            <optgroup label="📧 Email">
              <option value="send_email">Enviar Email (con Plantilla)</option>
              <option value="send_email_html">Enviar Email (HTML Directo)</option>
            </optgroup>
            <optgroup label="💬 WhatsApp">
              <option value="send_whatsapp">Enviar Plantilla de WhatsApp</option>
            </optgroup>
            <optgroup label="🔗 Integraciones">
              <option value="webhook">Webhook</option>
            </optgroup>
            <optgroup label="👥 Gestión de Contactos">
              <option value="add_to_list">Agregar a Lista</option>
              <option value="remove_from_list">Remover de Lista</option>
              <option value="update_field">Actualizar Campo del Contacto</option>
            </optgroup>
            <optgroup label="👤 Asignación">
              <option value="assign_to_sales_rep">Asignar a Equipo de Ventas</option>
            </optgroup>
          </Input>
          <small className="text-muted">
            Una vez seleccionado el tipo, podrás configurar los detalles específicos de la acción.
          </small>
        </FormGroup>
        {config.type && (
          <div className="mt-4">
            <hr />
            <h6 className="mb-3">
              <i className="ri-settings-3-line me-2"></i>
              Configuración: {config.type === 'send_email' ? 'Enviar Email (Plantilla)' :
                              config.type === 'send_email_html' ? 'Enviar Email (HTML)' :
                              config.type === 'send_whatsapp' ? 'Enviar WhatsApp' :
                              config.type === 'webhook' ? 'Webhook' :
                              config.type === 'add_to_list' ? 'Agregar a Lista' :
                              config.type === 'remove_from_list' ? 'Remover de Lista' :
                              config.type === 'update_field' ? 'Actualizar Campo' : config.type}
            </h6>
            {renderConfig()}
          </div>
        )}
      </>
    );
  }

  return renderConfig();
};

const getDefaultConfigForType = (type) => {
  switch (type) {
    case 'send_email':
      return { templateId: '', subject: '', emailProviderId: '' };
    case 'send_email_html':
      return { htmlContent: '', subject: '', emailProviderId: '', variables: {} };
    case 'send_whatsapp':
      return { whatsappTemplateId: '', phoneNumberId: '' };
    case 'webhook':
      return { webhookUrl: '', webhookMethod: 'POST', webhookHeaders: {}, webhookBody: '', webhookBodyType: 'json' };
    case 'add_to_list':
    case 'remove_from_list':
      return { listId: '' };
    case 'update_field':
      return { field: '', value: '' };
    case 'assign_to_sales_rep':
      return { salesTeamId: '' };
    default:
      return {};
  }
};

export default WorkflowBuilder;
